import React, { useState, useEffect, useRef } from 'react';
import { IconButton, Badge, Popover, Box, Typography, List, ListItem, ListItemText, ListItemIcon, Divider, Button } from '@mui/material';
import { Notifications as NotificationsIcon, Close as CloseIcon } from '@mui/icons-material';
import systemNotificationService from '../../services/systemNotificationService';

interface SystemNotificationBellProps {
  appId: string;
  clientId: string;
  environment?: string;
  userId?: string;
  userRole?: string;
}

interface Notification {
  _id: string;
  title: string;
  message: string;
  type: string;
  severity: string;
  visibilityLevel: string;
  isDismissible: boolean;
  publishedAt: string;
  userInteraction: {
    exists: boolean;
    read: boolean;
    acknowledged: boolean;
    dismissed: boolean;
  };
}

const SystemNotificationBell: React.FC<SystemNotificationBellProps> = ({
  appId,
  clientId,
  environment = 'production',
  userId,
  userRole
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [appId, clientId, environment, userId]);

  const fetchNotifications = async () => {
    try {
      const context = {
        clientId,
        appId,
        environment,
        role: userRole,
        externalUserId: userId
      };
      
      const data = await systemNotificationService.getActiveNotifications(context);
      
      // Filter for bell visibility level
      const bellNotifications = data.notifications.filter((n: Notification) => 
        n.visibilityLevel === 'bell' && !n.userInteraction.dismissed
      );
      
      setNotifications(bellNotifications);
      
      // Count unread
      const unread = bellNotifications.filter((n: Notification) => !n.userInteraction.read).length;
      setUnreadCount(unread);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setLoading(false);
    }
  };

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    
    // Mark all as read when opening
    if (notifications.length > 0 && userId) {
      const userContext = {
        externalUserId: userId,
        appId,
        clientId,
        environment,
        role: userRole
      };
      
      // Mark unread notifications as read
      const unreadNotifications = notifications.filter(n => !n.userInteraction.read);
      
      for (const notification of unreadNotifications) {
        try {
          await systemNotificationService.markAsRead(notification._id, userContext);
        } catch (error) {
          console.error('Error marking as read:', error);
        }
      }
      
      // Update local state
      setUnreadCount(0);
      setNotifications(prev => 
        prev.map(n => ({
          ...n,
          userInteraction: { ...n.userInteraction, read: true }
        }))
      );
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDismiss = async (notification: Notification, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (!userId) return;
    
    try {
      const userContext = {
        externalUserId: userId,
        appId,
        clientId,
        environment,
        role: userRole
      };
      
      await systemNotificationService.dismiss(notification._id, userContext);
      
      // Remove from list
      setNotifications(prev => prev.filter(n => n._id !== notification._id));
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  const getIcon = (type: string, severity: string) => {
    if (severity === 'critical') return '🔥';
    
    switch (type) {
      case 'maintenance':
        return '🔧';
      case 'incident':
        return '⚠️';
      case 'patch':
        return '🩹';
      case 'release':
        return '🚀';
      case 'security':
        return '🔒';
      default:
        return '📢';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '#f44336';
      case 'warning':
        return '#ff9800';
      case 'info':
      default:
        return '#2196f3';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleClick}
        aria-label="System notifications"
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 380,
            maxHeight: 500,
            mt: 1
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            System Notifications
          </Typography>
          {notifications.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
            </Typography>
          )}
        </Box>

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading...</Typography>
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ mb: 1, opacity: 0.3 }}>✓</Typography>
            <Typography color="text.secondary">No new notifications</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification._id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    alignItems: 'flex-start',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${getSeverityColor(notification.severity)}20`,
                        fontSize: 18
                      }}
                    >
                      {getIcon(notification.type, notification.severity)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 0.5
                          }}
                        >
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(notification.publishedAt)}
                        </Typography>
                      </>
                    }
                  />
                  {notification.isDismissible && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleDismiss(notification, e)}
                      sx={{ ml: 1 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};

export default SystemNotificationBell;
